import { PlayerName } from './PlayerName';
import { useState, useMemo, useId } from 'react';
import {
  ArrowLeft,
  Edit2,
  Minus,
  Plus,
  Search,
  TrendingDown,
  TrendingUp,
  UserPlus,
} from 'lucide-react';
import { ELO_LEVELS, getEloLevel } from '../../engine';
import { SortableHeader } from '../ui';
import { PlayerHistoryModal } from './PlayerHistoryModal';
import { PlayerEditModal } from './PlayerEditModal';
import { InvitePlayerModal } from './InvitePlayerModal';
import { JoinRequestsPanel } from './JoinRequestsPanel';
import { ClubMembersPanel } from './ClubMembersPanel';
import { PlayerLookupPanel } from './PlayerLookupPanel';
import { isApiMode } from '../../lib/api/baseUrl';
import { useT } from '../../i18n';
import { safeId } from '../ui/labelledControl';
import { isErasedPlayer } from '../../lib/playerName';

// ═══════════════════════════════════════════════════════════════════
// TWO TABS: «ALLE SPILLERE» AND «MEDLEMMER».
//
// The screen answers two different questions, and stacking both answers on one
// page made the second — who is a MEMBER of this club — a box somewhere above
// a long list. The owner asked for a page for each (2026-10-20): the player
// list is everybody the club works with (`GET /members/players`, #1060 — its
// roster plus the players in its own events and guests from other clubs); the
// roster is `tenant_players`, the club's own members and nobody else.
//
// ── THE JOIN QUEUE SITS ABOVE BOTH TABS, NOT ON ONE OF THEM ─────
//
// A pending request is work waiting for a human, and it renders nothing when
// nobody has asked (#710). Put behind the «Medlemmer» tab it would be invisible
// on the tab the screen opens on, and a club would learn of an applicant only by
// clicking a tab for another reason. Above the tabs it costs nothing when empty
// and cannot be missed when it is not.
//
// «Finn en spiller fra en annen klubb» (PlayerLookupPanel) adds somebody to the
// PLAYER LIST, so it lives on the «Alle spillere» tab with that list.
//
// ── OFF THE API THERE ARE NO TABS ───────────────────────────────────
//
// The legacy backend has no roster and no queue, so the «Medlemmer» tab would
// be empty. The screen then renders exactly as it did before the tabs existed.
// ═══════════════════════════════════════════════════════════════════

type PlayersTab = 'players' | 'members';
const TABS: readonly PlayersTab[] = ['players', 'members'];

export interface PlayersViewProps {
  /** The effective player DB (tenant-scoped) — the full list rendered/filtered/sorted. */
  effectivePlayerDB: any[];
  /** Archive of completed tournaments — used for the count card and forwarded to the history modal. */
  archive: any[];
  /** Single (non-tournament) matches — forwarded to the history modal. */
  singleMatches?: any[];
  /** Manual ELO adjustments — forwarded to the history modal. */
  eloAdjustments?: any[];
  /** Navigates back to the home hub. */
  onBack: () => void;
  /** Opens an archived tournament from the history/merits list. */
  onOpenArchived: (tournament: any) => void;
  /** Creates a custom player from the create modal. Truthy → the «Opprett spiller» button renders. */
  onAddCustomPlayer?: (data: any) => any;
  /** Updates a custom player from the edit modal. Truthy → the per-row edit button renders. */
  onUpdateCustomPlayer?: (id: any, data: any) => any;
  /** Deletes a custom player from the edit modal. */
  onDeleteCustomPlayer?: (id: any) => any;
  /**
   * Puts a player found by name into this session's list (#1060). Truthy, on the
   * API backend → the «find a player from another club» panel renders.
   */
  onAdoptPlayer?: (player: any) => void;
  /**
   * The live `Padel24Engine` wiring object. Not read
   * directly here — forwarded to `PlayerHistoryModal`, which reads runtime-mutable
   * `.elo.*` / `.settings.*` / `.getTournamentType` off it.
   */
  Padel24Engine: any;
  /**
   * Resolves the player's per-tournament history rows. deviation: this
   * is the module-level `getPlayerTournamentHistory` helper, which is not yet extracted
   * to src/engine; it is threaded in as a prop and forwarded to `PlayerHistoryModal`.
   */
  getPlayerTournamentHistory: (playerId: any, archive: any[], playerName?: any) => any[];
  /**
   * Resolves a player's final placement in a tournament. deviation: same as
   * `getPlayerTournamentHistory` above — a not-yet-extracted helper passed as a prop and
   * forwarded to `PlayerHistoryModal`.
   */
  calcPlayerPositionInTournament: (playerId: any, tournament: any) => any;
}

/**
 * Player-database view — stats cards, a search/gender/level/active filter row, a
 * sortable player table (ELO rank, level pill, 30-day trend, tournament count), and
 * the create/edit/history modals. Extracted verbatim from the original monolith (legacy/App.jsx);
 * markup/logic unchanged. Takes the live `Padel24Engine` object plus the two not-yet-extracted
 * helpers (`getPlayerTournamentHistory`, `calcPlayerPositionInTournament`) as props, all
 * three forwarded to `PlayerHistoryModal`.
 */
export function PlayersView({
  effectivePlayerDB,
  archive,
  singleMatches = [],
  eloAdjustments = [],
  onBack,
  onOpenArchived,
  onAddCustomPlayer,
  onUpdateCustomPlayer,
  onDeleteCustomPlayer,
  onAdoptPlayer,
  Padel24Engine,
  getPlayerTournamentHistory,
  calcPlayerPositionInTournament,
}: PlayersViewProps) {
  const t = useT();
  const [search, setSearch] = useState('');
  // Bumped when a join request is decided, so the member list re-reads.
  const [rosterVersion, setRosterVersion] = useState(0);
  // Which of the two pages is open. The player list is the default: it is what
  // the screen has always been, and the stats cards are about it.
  const [tab, setTab] = useState<PlayersTab>('players');
  const tabsId = safeId('players-tabs', useId());
  const hasTabs = isApiMode();
  const showPlayers = !hasTabs || tab === 'players';
  const showMembers = hasTabs && tab === 'members';
  const [sortBy, setSortBy] = useState('elo');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc'); // 'asc' | 'desc'

  // Click on a header — first time sets the sort, second time toggles the direction
  const handleSortClick = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(key);
      // Default direction: name is asc (A-Å), numbers are desc
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };
  const [genderFilter, setGenderFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all'); // 'all' or level id (l1-l9)
  const [activeOnly, setActiveOnly] = useState(false); // only players active in the last 30 days
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  // WHO IS BEING INVITED, if anyone (#185, #1061). Null closes the dialog.
  const [invitingPlayer, setInvitingPlayer] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);

  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;

  // ELO ranking for the entire database — independent of filter/sort.
  // Used for the "#" column, which always shows the player's position on the ELO list.
  const eloRankMap = useMemo(() => {
    const sorted = [...effectivePlayerDB].sort((a, b) => (b.elo || 0) - (a.elo || 0));
    const map = new Map();
    sorted.forEach((p, i) => map.set(p.id, i + 1));
    return map;
  }, [effectivePlayerDB]);

  // Number of players per level (respects the gender filter, but not the level/active
  // filters — so the numbers show how many you WOULD get by selecting the level).
  const levelCounts = useMemo(() => {
    const counts = {};
    ELO_LEVELS.forEach((l) => {
      counts[l.id] = 0;
    });
    let base = effectivePlayerDB;
    if (genderFilter !== 'all') base = base.filter((p) => p.gender === genderFilter);
    base.forEach((p) => {
      const lvl = getEloLevel(p.elo || 0);
      if (lvl) counts[lvl.id] = (counts[lvl.id] || 0) + 1;
    });
    return counts;
  }, [effectivePlayerDB, genderFilter]);

  // Filter + sort
  const thirtyDaysAgoTs = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const filteredPlayers = useMemo(() => {
    let list = [...effectivePlayerDB];

    if (genderFilter !== 'all') {
      list = list.filter((p) => p.gender === genderFilter);
    }
    if (levelFilter !== 'all') {
      list = list.filter((p) => getEloLevel(p.elo || 0)?.id === levelFilter);
    }
    if (activeOnly) {
      // Active = has played a match in the last 30 days
      list = list.filter((p) => (p.lastPlayedAt || 0) >= thirtyDaysAgoTs);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }

    list.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') cmp = a.name.localeCompare(b.name, 'nb');
      else if (sortBy === 'tournaments') cmp = (a.tournamentCount || 0) - (b.tournamentCount || 0);
      else if (sortBy === 'level')
        cmp = (getEloLevel(a.elo || 0)?.level || 0) - (getEloLevel(b.elo || 0)?.level || 0);
      else if (sortBy === 'change') {
        const ca = (a.elo || 0) - (a.yearStartElo ?? a.originalElo ?? 0);
        const cb = (b.elo || 0) - (b.yearStartElo ?? b.originalElo ?? 0);
        cmp = ca - cb;
      } else if (sortBy === 'yearStart')
        cmp = (a.yearStartElo ?? a.originalElo ?? 0) - (b.yearStartElo ?? b.originalElo ?? 0);
      else if (sortBy === 'gender') cmp = (a.gender || '').localeCompare(b.gender || '');
      else cmp = (a.elo || 0) - (b.elo || 0); // default: elo
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return list;
  }, [
    effectivePlayerDB,
    search,
    sortBy,
    sortDir,
    genderFilter,
    levelFilter,
    activeOnly,
    thirtyDaysAgoTs,
  ]);

  const totalPlayers = effectivePlayerDB.length;
  const playedCount = effectivePlayerDB.filter((p) => (p.tournamentCount || 0) > 0).length;
  const eloSum = effectivePlayerDB.reduce((s, p) => s + (p.elo || 0), 0);
  const avgElo = totalPlayers > 0 ? Math.round(eloSum / totalPlayers) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 slide-up">
      <button onClick={onBack} className="btn-ghost mb-4 -ml-2">
        <ArrowLeft size={14} /> {t('players.view.home')}
      </button>

      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold mb-1">{t('players.view.title')}</h1>
          <p className="text-ink-2 text-sm">{t('players.view.lead')}</p>
        </div>
        {onAddCustomPlayer && (
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <Plus size={14} /> {t('players.view.create')}
          </button>
        )}
      </div>

      {/* ── SØKNADER, ABOVE THE ROSTER (#710) ─────────────────────────
          Here because this is the screen a club already opens to manage who
          plays for them, and a request is the same question arriving from the
          other direction. It renders NOTHING when nobody has asked. */}
      <JoinRequestsPanel
        enabled={isApiMode()}
        onDecided={() => setRosterVersion((v) => v + 1)}
        // The club's full card when the list below holds the applicant — the
        // queue carries the legacy id the list is keyed on. Since #1067 that
        // list is the roster and the club's own events' players, so for most
        // applicants the panel shows its own card instead.
        onOpenPlayer={setSelectedPlayerId}
        hasPlayerCard={(id) => effectivePlayerDB.some((p) => p.id === id)}
      />

      {/* ── THE TABS (see the header). Arrow keys move between them, as the
          platform's role tabs do; the panel below carries the tab's name. */}
      {hasTabs && (
        <div
          role="tablist"
          aria-label={t('players.view.tabsLabel')}
          className="flex gap-1 border-b border-line mb-5"
        >
          {TABS.map((id) => (
            <button
              key={id}
              id={`${tabsId}-tab-${id}`}
              type="button"
              role="tab"
              aria-selected={tab === id}
              aria-controls={`${tabsId}-panel-${id}`}
              tabIndex={tab === id ? 0 : -1}
              onClick={() => setTab(id)}
              onKeyDown={(e) => {
                if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                e.preventDefault();
                const step = e.key === 'ArrowRight' ? 1 : -1;
                const next = TABS[(TABS.indexOf(id) + step + TABS.length) % TABS.length];
                setTab(next);
                document.getElementById(`${tabsId}-tab-${next}`)?.focus();
              }}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors focus-ring ${
                tab === id
                  ? 'border-lime-deep text-ink bg-lime-bg/40'
                  : 'border-transparent text-ink-2 hover:text-ink hover:bg-bg-soft'
              }`}
            >
              {id === 'players' ? t('players.view.tabPlayers') : t('players.view.tabMembers')}
            </button>
          ))}
        </div>
      )}

      {/* ── MEDLEMMER — the club's ROSTER, which the player list is not ──
          The player list is the shared `players` table; this is who is a
          member of THIS club, so an approval above shows up here. */}
      {showMembers && (
        <div
          id={`${tabsId}-panel-members`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-members`}
        >
          <ClubMembersPanel
            enabled={isApiMode()}
            version={rosterVersion}
            // «Last opp medlemsliste» — where the club may add players at all.
            // The people it created join this session's list the way a player
            // found by name does (#1060), and the roster re-reads.
            onImported={
              onAdoptPlayer
                ? (created) => {
                    for (const p of created) onAdoptPlayer(p);
                    setRosterVersion((v) => v + 1);
                  }
                : undefined
            }
          />
        </div>
      )}

      {showPlayers && (
        <div
          id={hasTabs ? `${tabsId}-panel-players` : undefined}
          role={hasTabs ? 'tabpanel' : undefined}
          aria-labelledby={hasTabs ? `${tabsId}-tab-players` : undefined}
        >
          {isApiMode() && onAdoptPlayer && (
            <PlayerLookupPanel
              knownIds={new Set(effectivePlayerDB.map((p) => String(p.id)))}
              onAdopt={onAdoptPlayer}
            />
          )}

          {/* Stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
            <div className="card p-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
                {t('players.view.totalPlayers')}
              </div>
              <div className="font-display text-2xl font-bold">{totalPlayers}</div>
            </div>
            <div className="card p-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
                {t('players.view.active')}
              </div>
              <div className="font-display text-2xl font-bold">{playedCount}</div>
              <div className="text-[11px] text-ink-3">{t('players.view.activeHint')}</div>
            </div>
            <div className="card p-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
                {t('players.view.avgElo')}
              </div>
              <div className="font-display text-2xl font-bold">{avgElo}</div>
            </div>
            <div className="card p-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
                {t('players.view.tournaments')}
              </div>
              <div className="font-display text-2xl font-bold">{archive.length}</div>
              <div className="text-[11px] text-ink-3">{t('players.view.tournamentsHint')}</div>
            </div>
          </div>

          {/* Filter row */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('players.view.searchPlaceholder')}
                className="input w-full text-sm"
                style={{ paddingLeft: '2.25rem' }}
              />
            </div>

            <div className="flex items-center gap-1 border border-line rounded-full bg-paper p-0.5 text-xs">
              {[
                { id: 'all', label: t('players.view.filterAll') },
                { id: 'F', label: t('players.view.filterWomen') },
                { id: 'M', label: t('players.view.filterMen') },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setGenderFilter(opt.id)}
                  className={`px-3 py-1 rounded-full font-medium transition-colors ${
                    genderFilter === opt.id ? 'bg-ink text-white' : 'text-ink-2 hover:bg-bg-soft'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Level filter */}
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="input text-xs py-1.5 cursor-pointer"
              style={{ width: 'auto', minWidth: '180px' }}
              title={t('players.view.levelFilterTitle')}
              aria-label={t('players.view.levelFilterTitle')}
            >
              <option value="all">
                {t('players.view.allLevels', { count: effectivePlayerDB.length })}
              </option>
              {ELO_LEVELS.map((lvl) => (
                <option key={lvl.id} value={lvl.id}>
                  {t('players.view.levelOption', {
                    level: lvl.level,
                    name: lvl.name,
                    count: levelCounts[lvl.id] || 0,
                  })}
                </option>
              ))}
            </select>

            {/* Active filter — only players who have played in the last 30 days */}
            <button
              onClick={() => setActiveOnly((v) => !v)}
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
              style={
                activeOnly
                  ? {
                      background: 'var(--lime-deep)',
                      color: '#fff',
                      borderColor: 'var(--lime-deep)',
                    }
                  : {
                      background: 'var(--paper)',
                      color: 'var(--ink-2)',
                      borderColor: 'var(--line)',
                    }
              }
              title={t('players.view.activeOnlyTitle')}
            >
              {t('players.view.activeOnly')}
            </button>
          </div>

          {/* Player list.

          THE NAME COLUMN HAS A FLOOR, AND THE LIST SCROLLS ON ITS OWN (#916,
          WCAG 1.4.10). The name column was a bare `1fr` beside some 640 px
          of fixed columns, gaps and padding, so at phone width it got 0 px and
          the player's name — the one cell that tells the rows apart — vanished,
          while the rest of the row was cut off by the card. Now the name column
          never goes below 10rem, and a row wider than the card scrolls sideways
          inside it (a data table may; the page itself does not). The card's
          rounded corners still clip, as `overflow-hidden` did. `min-w-fit` on
          the header and each row makes them as wide as their columns, so the
          header background, a row's border and its hover cover all of it.
          Where the card is wide enough (the portal on a desktop) nothing moves:
          10rem is far below what `1fr` gets there. */}
          <div className="card overflow-x-auto">
            <div
              className="min-w-fit grid gap-2 px-4 py-2.5 bg-bg-soft text-[11px] font-mono font-semibold text-ink-2 uppercase tracking-wider"
              style={{
                gridTemplateColumns: '40px minmax(10rem, 1fr) 80px 60px 60px 70px 80px 90px 64px',
              }}
            >
              <div>#</div>
              <SortableHeader
                sortKey="name"
                currentSort={sortBy}
                sortDir={sortDir}
                onClick={handleSortClick}
              >
                {t('players.view.colPlayer')}
              </SortableHeader>
              <SortableHeader
                sortKey="level"
                currentSort={sortBy}
                sortDir={sortDir}
                onClick={handleSortClick}
              >
                {t('players.view.colLevel')}
              </SortableHeader>
              <SortableHeader
                sortKey="gender"
                currentSort={sortBy}
                sortDir={sortDir}
                onClick={handleSortClick}
                align="center"
              >
                {t('players.view.colGender')}
              </SortableHeader>
              <SortableHeader
                sortKey="yearStart"
                currentSort={sortBy}
                sortDir={sortDir}
                onClick={handleSortClick}
                align="right"
                title={t('players.view.colEloYearTitle', { year: String(lastYear) })}
              >
                {t('players.view.colEloYear', { year: String(lastYear) })}
              </SortableHeader>
              <SortableHeader
                sortKey="elo"
                currentSort={sortBy}
                sortDir={sortDir}
                onClick={handleSortClick}
                align="right"
              >
                {t('players.view.colNow')}
              </SortableHeader>
              <div
                className="flex items-center justify-center font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-2"
                title={t('players.view.trendTitle')}
              >
                {t('players.view.colTrend')}
              </div>
              <SortableHeader
                sortKey="tournaments"
                currentSort={sortBy}
                sortDir={sortDir}
                onClick={handleSortClick}
                align="right"
              >
                {t('players.view.colTournaments')}
              </SortableHeader>
              <div></div>
            </div>
            {filteredPlayers.map((p, i) => {
              const yearStart = p.yearStartElo ?? p.originalElo ?? 0;
              const tournaments = p.tournamentCount || 0;
              const level = getEloLevel(p.elo || 0);
              // Trend over the last 30 days: compare current ELO against elo30dAgo.
              // null if the player has not played within the 30-day window.
              const trend30d = p.elo30dAgo != null ? (p.elo || 0) - p.elo30dAgo : null;
              return (
                <div
                  key={p.id}
                  className={`min-w-fit grid gap-2 px-4 py-2 items-center text-sm hover:bg-bg-soft/50 transition-colors ${i > 0 ? 'border-t border-line-soft' : ''}`}
                  style={{
                    gridTemplateColumns:
                      '40px minmax(10rem, 1fr) 80px 60px 60px 70px 80px 90px 64px',
                  }}
                >
                  <div className="font-mono text-ink-3 text-xs" title={t('players.view.rankTitle')}>
                    {eloRankMap.get(p.id) || '–'}
                  </div>
                  {/* THE NAME IS THE KEYBOARD WAY IN (#900). Every cell in the row
                  opens the profile on a click, but they were all <div>s, so the
                  profile could not be reached from a keyboard at all. The name
                  is a button now, and it is where focus returns when the
                  profile closes. The other cells keep their click as a pointer
                  convenience: they only repeat the name button, so they are
                  role="presentation", not controls of their own. */}
                  <button
                    type="button"
                    onClick={() => setSelectedPlayerId(p.id)}
                    className="font-medium truncate cursor-pointer flex items-center gap-1.5 min-w-0 text-left rounded focus-ring"
                  >
                    <span className="truncate">
                      <PlayerName player={p} />
                    </span>
                    {!p.isSystem && p.isCustom !== false && (
                      <span
                        className="font-mono text-[9px] font-bold px-1 py-0.5 rounded bg-lime-bg text-lime-deep flex-shrink-0"
                        title={t('players.view.customPlayer')}
                      >
                        {t('players.view.newBadge')}
                      </span>
                    )}
                  </button>
                  {/* Level pill */}
                  <div
                    role="presentation"
                    className="cursor-pointer"
                    onClick={() => setSelectedPlayerId(p.id)}
                  >
                    <span
                      className="inline-flex items-center font-mono text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: level.color, color: level.text }}
                      title={t('players.view.levelPillTitle', {
                        level: level.level,
                        name: level.name,
                        short: level.short,
                      })}
                    >
                      {t('players.common.level', { level: level.level })}
                    </span>
                  </div>
                  <div
                    role="presentation"
                    className="text-center cursor-pointer"
                    onClick={() => setSelectedPlayerId(p.id)}
                  >
                    <span className="text-xs text-ink-2">
                      {/* Erasure cleared the gender; «Mann», the default for a
                          missing one, would be a guess (#1321). */}
                      {isErasedPlayer(p)
                        ? null
                        : p.gender === 'F'
                          ? t('players.view.genderWoman')
                          : t('players.view.genderMan')}
                    </span>
                  </div>
                  <div
                    role="presentation"
                    className="text-right font-mono text-xs text-ink-3 cursor-pointer"
                    onClick={() => setSelectedPlayerId(p.id)}
                  >
                    {yearStart}
                  </div>
                  <div
                    role="presentation"
                    className="text-right font-mono text-sm font-semibold cursor-pointer"
                    onClick={() => setSelectedPlayerId(p.id)}
                  >
                    {p.elo}
                  </div>
                  {/* Trend arrow over the last 30 days */}
                  <div
                    role="presentation"
                    className="flex items-center justify-center cursor-pointer"
                    onClick={() => setSelectedPlayerId(p.id)}
                  >
                    {trend30d == null ? (
                      <span className="text-ink-4 text-xs" title={t('players.view.noTrend')}>
                        –
                      </span>
                    ) : trend30d > 5 ? (
                      <span
                        className="flex items-center gap-0.5 text-lime-deep"
                        title={t('players.view.trendUp', { change: String(trend30d) })}
                      >
                        <TrendingUp size={13} />
                        <span className="font-mono text-[10px] font-bold">+{trend30d}</span>
                      </span>
                    ) : trend30d < -5 ? (
                      <span
                        className="flex items-center gap-0.5 text-rose"
                        title={t('players.view.trendDown', { change: String(trend30d) })}
                      >
                        <TrendingDown size={13} />
                        <span className="font-mono text-[10px] font-bold">{trend30d}</span>
                      </span>
                    ) : (
                      <span
                        className="flex items-center gap-0.5 text-ink-3"
                        title={t('players.view.trendFlat', {
                          change: `${trend30d > 0 ? '+' : ''}${trend30d}`,
                        })}
                      >
                        <Minus size={13} />
                      </span>
                    )}
                  </div>
                  <div
                    role="presentation"
                    className="text-right font-mono text-xs text-ink-2 cursor-pointer"
                    onClick={() => setSelectedPlayerId(p.id)}
                  >
                    {tournaments > 0 ? (
                      tournaments
                    ) : (
                      <span className="text-ink-4 italic">
                        {t('players.view.noTournamentsCell')}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-end gap-1">
                    {/* INVITE — beside edit, because it belongs where the club's
                    people are managed. It sends an e-mail invitation; the player
                    becomes a member only by accepting it (#1061). A placeholder
                    is not a person, so it is not offered one. */}
                    {!p.isPlaceholder && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setInvitingPlayer({
                            id: String(p.id),
                            name: String(p.name ?? ''),
                            email: typeof p.email === 'string' ? p.email : '',
                          });
                        }}
                        className="p-1.5 rounded text-ink-3 hover:bg-bg-soft hover:text-ink transition-colors"
                        title={t('players.view.invitePlayer')}
                        data-testid={`invite-player-${p.id}`}
                      >
                        <UserPlus size={12} />
                      </button>
                    )}
                    {onUpdateCustomPlayer && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingPlayer(p);
                        }}
                        className="p-1.5 rounded text-ink-3 hover:bg-bg-soft hover:text-ink transition-colors"
                        title={t('players.view.editPlayer')}
                      >
                        <Edit2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredPlayers.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-ink-3 italic">
                {t('players.view.noSearchMatches')}
              </div>
            )}
          </div>
        </div>
      )}

      {invitingPlayer && (
        <InvitePlayerModal
          playerId={invitingPlayer.id}
          playerName={invitingPlayer.name}
          playerEmail={invitingPlayer.email}
          onClose={() => setInvitingPlayer(null)}
        />
      )}

      {/* Player history popup */}
      {selectedPlayerId && (
        <PlayerHistoryModal
          playerId={selectedPlayerId}
          effectivePlayerDB={effectivePlayerDB}
          archive={archive}
          singleMatches={singleMatches}
          eloAdjustments={eloAdjustments}
          onClose={() => setSelectedPlayerId(null)}
          onOpenArchived={onOpenArchived}
          Padel24Engine={Padel24Engine}
          getPlayerTournamentHistory={getPlayerTournamentHistory}
          calcPlayerPositionInTournament={calcPlayerPositionInTournament}
        />
      )}

      {/* Create player modal */}
      {showCreateModal && (
        <PlayerEditModal
          mode="create"
          onClose={() => setShowCreateModal(false)}
          onSave={async (data) => {
            await onAddCustomPlayer(data);
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Edit player modal */}
      {editingPlayer && (
        <PlayerEditModal
          mode="edit"
          player={editingPlayer}
          onClose={() => setEditingPlayer(null)}
          onSave={async (data) => {
            await onUpdateCustomPlayer(editingPlayer.id, data);
            setEditingPlayer(null);
          }}
          onDelete={async () => {
            await onDeleteCustomPlayer(editingPlayer.id);
            setEditingPlayer(null);
          }}
        />
      )}
    </div>
  );
}
